import React from 'react';
import { Address } from '../types';

interface Country {
  iso_2: string;
  display_name: string;
}

interface AddressFormProps {
  address: Address;
  onAddressChange: (address: Partial<Address>) => void;
  errors: Record<string, string>;
  fieldPrefix: string;
  countries?: Country[];
  isLoadingCountries?: boolean;
}

const AddressForm: React.FC<AddressFormProps> = ({
  address,
  onAddressChange,
  errors,
  fieldPrefix,
  countries = [],
  isLoadingCountries = false,
}) => {
  const getFieldError = (fieldName: string) => {
    return errors[`${fieldPrefix}${fieldName}`];
  };

  return (
    <div className="address-form">
      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}first_name`}>First Name *</label>
          <input
            id={`${fieldPrefix}first_name`}
            name="first_name"
            type="text"
            value={address.first_name}
            onChange={(e) => onAddressChange({ first_name: e.target.value })}
            className={getFieldError('first_name') ? 'error' : ''}
            placeholder="John"
            aria-required="true"
            aria-invalid={!!getFieldError('first_name')}
          />
          {getFieldError('first_name') && (
            <span className="error-text" role="alert">{getFieldError('first_name')}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`${fieldPrefix}last_name`}>Last Name *</label>
          <input
            id={`${fieldPrefix}last_name`}
            name="last_name"
            type="text"
            value={address.last_name}
            onChange={(e) => onAddressChange({ last_name: e.target.value })}
            className={getFieldError('last_name') ? 'error' : ''}
            placeholder="Doe"
            aria-required="true"
            aria-invalid={!!getFieldError('last_name')}
          />
          {getFieldError('last_name') && (
            <span className="error-text" role="alert">{getFieldError('last_name')}</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}company`}>Company</label>
        <input
          id={`${fieldPrefix}company`}
          type="text"
          value={address.company || ''}
          onChange={(e) => onAddressChange({ company: e.target.value })}
          placeholder="Your Company (optional)"
        />
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}address_1`}>Address *</label>
        <input
          id={`${fieldPrefix}address_1`}
          name="address_1"
          type="text"
          value={address.address_1}
          onChange={(e) => onAddressChange({ address_1: e.target.value })}
          className={getFieldError('address_1') ? 'error' : ''}
          placeholder="123 Main Street"
          aria-required="true"
          aria-invalid={!!getFieldError('address_1')}
        />
        {getFieldError('address_1') && (
          <span className="error-text" role="alert">{getFieldError('address_1')}</span>
        )}
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}address_2`}>Apartment, suite, etc.</label>
        <input
          id={`${fieldPrefix}address_2`}
          type="text"
          value={address.address_2 || ''}
          onChange={(e) => onAddressChange({ address_2: e.target.value })}
          placeholder="Apartment, suite, unit, etc. (optional)"
        />
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}city`}>City *</label>
          <input
            id={`${fieldPrefix}city`}
            name="city"
            type="text"
            value={address.city}
            onChange={(e) => onAddressChange({ city: e.target.value })}
            className={getFieldError('city') ? 'error' : ''}
            placeholder="New York"
            aria-required="true"
            aria-invalid={!!getFieldError('city')}
          />
          {getFieldError('city') && (
            <span className="error-text" role="alert">{getFieldError('city')}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`${fieldPrefix}province`}>State/Province</label>
          <input
            id={`${fieldPrefix}province`}
            type="text"
            value={address.province || ''}
            onChange={(e) => onAddressChange({ province: e.target.value })}
            placeholder="NY"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor={`${fieldPrefix}postal_code`}>Postal Code *</label>
          <input
            id={`${fieldPrefix}postal_code`}
            name="postal_code"
            type="text"
            value={address.postal_code}
            onChange={(e) => onAddressChange({ postal_code: e.target.value })}
            className={getFieldError('postal_code') ? 'error' : ''}
            placeholder="10001"
            aria-required="true"
            aria-invalid={!!getFieldError('postal_code')}
          />
          {getFieldError('postal_code') && (
            <span className="error-text" role="alert">{getFieldError('postal_code')}</span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor={`${fieldPrefix}country_code`}>Country *</label>
          <select
            id={`${fieldPrefix}country_code`}
            name="country_code"
            value={address.country_code}
            onChange={(e) => onAddressChange({ country_code: e.target.value })}
            className={getFieldError('country_code') ? 'error' : ''}
            disabled={isLoadingCountries || countries.length === 0}
            aria-required="true"
            aria-invalid={!!getFieldError('country_code')}
          >
            <option value="">
              {isLoadingCountries
                ? 'Loading countries...'
                : countries.length === 0
                  ? 'No countries available'
                  : 'Select Country'}
            </option>
            {countries.map((country) => (
              <option key={country.iso_2} value={country.iso_2}>
                {country.display_name}
              </option>
            ))}
          </select>
          {getFieldError('country_code') && (
            <span className="error-text" role="alert">{getFieldError('country_code')}</span>
          )}
        </div>
      </div>

      <div className="form-group">
        <label htmlFor={`${fieldPrefix}phone`}>Phone</label>
        <input
          id={`${fieldPrefix}phone`}
          type="tel"
          value={address.phone || ''}
          onChange={(e) => onAddressChange({ phone: e.target.value })}
          placeholder="+1 (555) 123-4567"
        />
      </div>
    </div>
  );
};

export default AddressForm;